import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Sparkles, PlusCircle, BarChart3, Shield, Send, CheckCircle2,
  ArrowRight, ArrowLeft, Image as ImageIcon, MapPin, Tag, Calendar
} from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    title: '¡Bienvenido al Panel Organizador! 🎉',
    subtitle: 'Te vamos a mostrar todo lo que podés hacer en 5 pasos rápidos',
    content: (
      <div className="space-y-4">
        <p className="text-[#525252]">
          Como organizador de Mi Tribu vas a poder:
        </p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-[#0A0A0A]">Crear y publicar eventos en Salta y el NOA</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-[#0A0A0A]">Ver analytics en tiempo real de tus eventos</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-[#0A0A0A]">Recibir RSVPs y vender entradas</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-[#0A0A0A]">Proteger tu cuenta con autenticación 2FA</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    id: 'create',
    icon: PlusCircle,
    title: 'Paso 1: Crear tu primer evento',
    subtitle: 'Hacé click en el botón "+ Crear evento" arriba a la derecha',
    content: (
      <div className="space-y-4">
        <p className="text-[#525252]">El formulario te va a pedir:</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            <Calendar className="w-4 h-4 text-primary" />
            <span className="text-sm text-[#0A0A0A]">Título y descripción</span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            <Tag className="w-4 h-4 text-primary" />
            <span className="text-sm text-[#0A0A0A]">Categoría</span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="text-sm text-[#0A0A0A]">Ubicación</span>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            <ImageIcon className="w-4 h-4 text-primary" />
            <span className="text-sm text-[#0A0A0A]">Imagen (vertical)</span>
          </div>
        </div>
        <p className="text-sm text-[#737373] italic">
          💡 Tip: Las imágenes en formato 4:5 (vertical tipo Instagram) se ven mejor en la app
        </p>
      </div>
    )
  },
  {
    id: 'states',
    icon: Send,
    title: 'Paso 2: Estados del evento',
    subtitle: 'Tu evento pasa por diferentes estados antes de publicarse',
    content: (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 bg-[#F5F5F5] rounded-lg">
          <span className="px-2 py-1 bg-[#E5E5E5] text-[#737373] rounded text-xs font-semibold">Borrador</span>
          <span className="text-sm text-[#0A0A0A]">Estás editando, no es público</span>
        </div>
        <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
          <span className="px-2 py-1 bg-yellow-500/10 text-yellow-700 rounded text-xs font-semibold">En revisión</span>
          <span className="text-sm text-[#0A0A0A]">Admin lo está revisando</span>
        </div>
        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
          <span className="px-2 py-1 bg-green-500/10 text-green-700 rounded text-xs font-semibold">Publicado</span>
          <span className="text-sm text-[#0A0A0A]">¡Visible para todos! 🎉</span>
        </div>
        <p className="text-sm text-[#737373] mt-3">
          Después de crear el evento, hacé click en <strong className="text-primary">"Enviar"</strong> para enviarlo a revisión del admin.
        </p>
      </div>
    )
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Paso 3: Mirá tus Analytics',
    subtitle: 'En la tab "Analytics" tenés gráficos en tiempo real',
    content: (
      <div className="space-y-4">
        <p className="text-[#525252]">Vas a poder ver:</p>
        <ul className="space-y-2">
          <li className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            RSVPs por mes (gráfico de líneas)
          </li>
          <li className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            Ingresos en ARS por mes
          </li>
          <li className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            Eventos por categoría
          </li>
          <li className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            Top 5 eventos más vistos
          </li>
          <li className="flex items-center gap-2 text-[#0A0A0A]">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            Distribución por estado (pie chart)
          </li>
        </ul>
      </div>
    )
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Paso 4: Protegé tu cuenta',
    subtitle: 'Activá 2FA para asegurar tu cuenta de organizador',
    content: (
      <div className="space-y-4">
        <p className="text-[#525252]">
          En la tab "Seguridad" podés activar la autenticación de dos factores (2FA) usando Google Authenticator, Authy, o cualquier app TOTP.
        </p>
        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-4">
          <p className="text-sm text-[#0A0A0A]">
            <strong>¿Por qué activar 2FA?</strong>
          </p>
          <ul className="mt-2 space-y-1 text-sm text-[#525252]">
            <li>• Protege tu cuenta aunque te roben la contraseña</li>
            <li>• Suma confianza a tu marca como organizador</li>
            <li>• Te prepara para cuando manejes pagos reales</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 'ready',
    icon: Sparkles,
    title: '¡Listo para empezar! 🚀',
    subtitle: 'Ya conocés todo lo que necesitás',
    content: (
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-[#FFF7ED] to-white border border-[#FED7AA] rounded-lg p-6 text-center">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-3" />
          <p className="text-lg font-heading font-bold text-[#0A0A0A]">
            Tu primera acción
          </p>
          <p className="text-sm text-[#525252] mt-2">
            Crear tu primer evento haciendo click en el botón "+ Crear evento" arriba a la derecha
          </p>
        </div>
        <p className="text-sm text-[#737373] text-center">
          Si tenés dudas, podés volver a ver este tutorial desde el menú de tu perfil.
        </p>
      </div>
    )
  }
];

const OrganizerOnboarding = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  const handleNext = () => {
    if (isLast) {
      onClose(true); // mark as completed
    } else {
      setStep(step + 1);
    }
  };

  const handleSkip = () => {
    onClose(true);
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-white border-[#E5E5E5] max-w-2xl p-0" data-testid="organizer-onboarding">
        <DialogTitle className="sr-only">Bienvenido a Mi Tribu Organizador</DialogTitle>
        {/* Progress bar */}
        <div className="h-1 bg-[#F5F5F5] rounded-t-lg overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            data-testid="onboarding-progress"
          />
        </div>

        {/* Header */}
        <div className="px-8 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-[#737373] font-medium">PASO {step + 1} DE {STEPS.length}</p>
              <h2 className="text-xl font-heading font-bold text-[#0A0A0A]" data-testid="onboarding-title">
                {current.title}
              </h2>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors"
            data-testid="skip-onboarding"
          >
            Saltar
          </button>
        </div>

        {/* Subtitle */}
        <div className="px-8 pb-4">
          <p className="text-[#525252]">{current.subtitle}</p>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 min-h-[280px]">
          {current.content}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-[#E5E5E5] flex items-center justify-between">
          <Button
            onClick={() => setStep(step - 1)}
            disabled={isFirst}
            variant="outline"
            className="border-[#E5E5E5] text-[#0A0A0A]"
            data-testid="onboarding-prev"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>

          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === step ? 'bg-primary' : i < step ? 'bg-primary/40' : 'bg-[#E5E5E5]'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="onboarding-next"
          >
            {isLast ? '¡Empezar!' : 'Siguiente'}
            {!isLast && <ArrowRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizerOnboarding;
